/* eslint no-param-reassign: ["error", { "props": false }] */
import { merge } from 'lodash';
import http from '@/http';

import RestModule from '@/store/rest.module';
import { resourceReponseDeserializer, resourceErrorsReponseDeserializer, resourceSerializer } from '@/utils/json-api';

const defaultState = () => ({
  pending: false,
  one: undefined,
  included: undefined,
  etag: undefined,
  error: undefined,
});

export default ({
  namespace, url, module = {}, server = {}, props = {},
}) => {
  const RESOURCE = namespace.toUpperCase().split('/').join('_').slice(0, -1);
  const SET_RESOURCE_PENDING = `SET_${RESOURCE}_PENDING`;
  const SET_RESOURCE_SUCCESS = `SET_${RESOURCE}_SUCCESS`;
  const SET_RESOURCE_FAILURE = `SET_${RESOURCE}_FAILURE`;

  const { id: IdModule = () => ({}) } = module;
  const { attributes = [] } = server;
  const { id: resourceId } = props;

  return merge({
    namespaced: true,
    modules: {
      Versions: RestModule({ namespace: `${namespace}/Versions`, url: `${url}/${resourceId}/versions` }),
    },
    state: defaultState,
    getters: {
    },
    mutations: {
      [SET_RESOURCE_PENDING](state) {
        state.pending = true;
        state.error = null;
      },
      [SET_RESOURCE_SUCCESS](state, { data, headers }) {
        const { etag } = headers;

        if (!etag || state.etag !== etag) {
          state.etag = etag;
          const { data: one, included } = data;
          state.one = one;
          state.included = included;
        }
        state.pending = false;
      },
      [SET_RESOURCE_FAILURE](state, error) {
        state.error = error;
        state.pending = false;
      },
    },
    actions: {
      async fetchResource({ commit }) {
        commit(SET_RESOURCE_PENDING);
        try {
          const response = (await http.get(`${url}/${resourceId}`, {
            'axios-debounce': { id: `fetchResource_${namespace}` },
          })) |> (_ => resourceReponseDeserializer(_, attributes));

          commit(SET_RESOURCE_SUCCESS, response);
          return response;
        } catch (error) {
          if (http.isCancel(error)) {
            // canceled by next request
            return undefined;
          }
          commit(SET_RESOURCE_FAILURE, error);
          throw error;
        }
      },
      async updateResource({ commit, state: { one } }, { data }) {
        try {
          const { id } = one;
          const serialized = resourceSerializer(data, attributes);
          let response = await http.put(`${url}/${id}`, serialized);
          response = resourceReponseDeserializer(response, attributes);
          commit(SET_RESOURCE_SUCCESS, response);
          return response;
        } catch (error) {
          const pError = resourceErrorsReponseDeserializer(error, attributes);
          commit(SET_RESOURCE_FAILURE, pError);
          throw pError;
        }
      },
      async deleteResource({ commit, state: { one } }) {
        try {
          const { id } = one;
          let response = await http.delete(`${url}/${id}`);
          response = resourceReponseDeserializer(response, attributes);
          commit(SET_RESOURCE_SUCCESS, response);
          return response;
        } catch (error) {
          const pError = resourceErrorsReponseDeserializer(error, attributes);
          commit(SET_RESOURCE_FAILURE, pError);
          throw pError;
        }
      },
      async restoreResource({ commit, state: { one } }) {
        try {
          const { id } = one;
          let response = await http.patch(`${url}/${id}/restore`);
          response = resourceReponseDeserializer(response, attributes);
          commit(SET_RESOURCE_SUCCESS, response);
          return response;
        } catch (error) {
          const pError = resourceErrorsReponseDeserializer(error, attributes);
          commit(SET_RESOURCE_FAILURE, pError);
          throw pError;
        }
      },
    },
  }, IdModule({ namespace, url, props }));
};
